import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Constants } from "../data/constants";
import { Observable, tap } from "rxjs";
import { FilterListAdvertisingCompaign } from "../models/advertising-campaigns/filter-list-advertising-compaign.interface";
import { ResultFilterAdvertisingCompaign } from "../models/advertising-campaigns/result-filter-advertising-compaign.interface";
import { CampaignStatusEnum } from "../enum/campaign-status";
import { SuccessDto } from "../models/success-dto";
import { BestAdvertisingCampaign } from "../models/advertising-campaigns/best-advertising-campaign.interface";
import { AdPaymentDetailsInterface } from "../models/advertising-campaigns/ad-payment-details.interface";
import { AdsPublisherDetailsAndStatus } from "../models/advertising-campaigns/ads-publisher-details-and-status.interface";
import { FilterCalendar } from "../models/advertising-campaigns/filter-calendar.interface";
import {
  AdvertisingCampaignInterface,
  CreateUpdateAdvertisingCampaignInterface,
} from "../models/advertising-campaigns/advertising-campaign.interface";
import { CreateUpdateAdvertisingCampaignByLocationPartner } from "../models/advertising-campaigns/createUpdateAdvertisingCampaignByLocationPartner";

@Injectable({
  providedIn: "root",
})
export class AdvertisingCampaignService {
  constructor(private http: HttpClient) {}
  getLatestAdvertisingCampaigns(): Observable<AdvertisingCampaignInterface[]> {
    return this.http.get<AdvertisingCampaignInterface[]>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/latestAds/token`
    );
  }
  addAdvertisingCampaign(
    campaignDto: CreateUpdateAdvertisingCampaignInterface
  ): Observable<SuccessDto> {
    return this.http.post<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/create`,
      campaignDto
    );
  }
  filterAdvertisingCompaign(
    filter: FilterListAdvertisingCompaign,
    refreshCache: boolean
  ): Observable<ResultFilterAdvertisingCompaign> {
    return this.http.post<ResultFilterAdvertisingCompaign>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/refreshCache/${refreshCache}/filter`,
      filter
    );
  }

  getAdvertisingCompaignByHashedId(
    campaignHashedId: string | null
  ): Observable<AdvertisingCampaignInterface | null> {
    return this.http.get<AdvertisingCampaignInterface | null>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/${campaignHashedId}/details`
    );
  }
  updateAdCampaignDetails(
    campaignHashedId: string,
    adCompaign: CreateUpdateAdvertisingCampaignInterface | null
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/update/${campaignHashedId}`,
      adCompaign
    );
  }

  invokeUpadateAdPaymentDetails(
    payment: AdPaymentDetailsInterface
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/updatePayment`,
      payment
    );
  }

  updateAdCampaignStatus(
    campaignHashedId: string | null,
    status: CampaignStatusEnum | null
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/status/${campaignHashedId}/${status}`,
      null
    );
  }
  updateAdvertisingCampaignStatus(
    campaignHashedId: string,
    status: string
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/update/status/${campaignHashedId}/${status}`,
      null
    );
  }
  deleteAdCampaignByHashedId(campaignHashedId: string): Observable<SuccessDto> {
    return this.http.delete<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/${campaignHashedId}`
    );
  }
  bestAdvertisingCampaignResult(
    isRefreshCache: boolean
  ): Observable<BestAdvertisingCampaign> {
    return this.http.get<BestAdvertisingCampaign>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/bestAdvertisingCampaign/${isRefreshCache}`
    );
  }

  getAllAdsByStatusAndPublisherRole(
    filterCalendar: FilterCalendar
  ): Observable<AdvertisingCampaignInterface[]> {
    return this.http.post<AdvertisingCampaignInterface[]>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/allAdsForCalendar`,
      filterCalendar
    );
  }
  addSynthesisReport(
    campaignHashedId: string,
    synthesisReport: string | null
  ): Observable<SuccessDto> {
    return this.http.post<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/addSynthesisReport/${campaignHashedId}`,
      synthesisReport
    );
  }
  getAdvertiserByAdTitle(title: string[]): Observable<string[]> {
    return this.http.get<string[]>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/user/adTitle/${title}`
    );
  }
  getStatusesByAdTiles(
    campaignsHashedIds: string[]
  ): Observable<AdsPublisherDetailsAndStatus | null> {
    return this.http.post<AdsPublisherDetailsAndStatus | null>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/publisher/status`,
      campaignsHashedIds
    );
  }

  uploadJustificationFile(
    campaignHashedId: string,
    justificationFile: string | null
  ): Observable<SuccessDto | null> {
    return this.http.post<SuccessDto | null>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/uploadJustificationFile/${campaignHashedId}`,
      justificationFile
    );
  }

  uploadResponsesFile(
    campaignHashedId: string,
    responsesFile: string | null
  ): Observable<SuccessDto | null> {
    return this.http.post<SuccessDto | null>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/uploadResponsesFile/${campaignHashedId}`,
      responsesFile
    );
  }

  downloadLiveResponses(
    formId: string
  ) {
    return this.http.get(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/downloadLiveResponses/${formId}`, 
      { observe: "response", responseType: "blob" }
    ).pipe(
      tap(response => {
        if (response && response.body){

          // Create download link and trigger download
          const blob = new Blob([response.body], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          
          // Get filename from content-disposition header or use default
          const contentDisposition = response.headers.get('content-disposition');
          let filename = `responses-${formId}.csv`;
          
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch && filenameMatch.length === 2) {
              filename = filenameMatch[1];
            }
          }
          
          // Create temporary link and trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          
          // Clean up
          window.URL.revokeObjectURL(url);
        }
      })
    );
  }

  getAdvertiserAds(email: string): Observable<string[]> {
    return this.http.get<string[]>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/advertiserAds/` + email
    );
  }

  UpdateTotalHt(
    campaignHashedId: string | null,
    totalHT: number | null
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/totalHT/${campaignHashedId}`,
      totalHT
    );
  }

  createAdvertisingCampaignByLocation(
    campaignDto: CreateUpdateAdvertisingCampaignByLocationPartner
  ): Observable<SuccessDto> {
    return this.http.post<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/by-location`,
      campaignDto
    );
  }

  updateAdvertisingCampaignForLocation(
    hashedId: string,
    campaignDto: CreateUpdateAdvertisingCampaignByLocationPartner
  ): Observable<SuccessDto> {
    return this.http.patch<SuccessDto>(
      `${
        environment.APP_PORT + Constants.ADVERTISING_CAMPAIGN_ENDPOINT
      }/${hashedId}`,
      campaignDto
    );
  }
}
